/**
 * Reusable ECharts-based chart components for analytics views
 * Title + InfoTooltip rendered as HTML header; ECharts handles only the chart area
 */
import * as echarts from 'echarts';
import { useEffect, useRef } from 'preact/hooks';
import { COLORS, chartTheme } from './chartTheme.js';
import { InfoTooltip } from './InfoTooltip.jsx';

function ChartHeader({ title, infoKey }) {
  return (
    <div className="analytics-chart-header">
      <span className="analytics-chart-title">{title}</span>
      {infoKey && <InfoTooltip infoKey={infoKey} />}
    </div>
  );
}

/**
 * Build ECharts markArea + markLine that visually flag the cutoff from which a
 * metric becomes reliable. Used for the heartbeat-union active-demo metrics:
 * buckets before `reliableFrom` (heartbeat data started 2026-05-18) fall back
 * to last_login and undercount, so we shade them and draw a labelled boundary.
 * Returns null if the cutoff is outside the visible range.
 */
function reliabilityMarkers(data, xKey, xData, reliableFrom, label = 'Heartbeat-accurate from here') {
  if (!reliableFrom || !data || !data.length) return null;
  const cutoff = new Date(reliableFrom);
  const idx = data.findIndex((d) => new Date(d[xKey]) >= cutoff);
  if (idx <= 0) return null; // whole range reliable (idx 0) or cutoff not in range (-1)
  return {
    markArea: {
      silent: true,
      itemStyle: { color: 'rgba(148, 163, 184, 0.10)' },
      data: [[{ xAxis: xData[0] }, { xAxis: xData[idx - 1] }]],
    },
    markLine: {
      silent: true,
      symbol: 'none',
      lineStyle: { color: COLORS.cyan, type: 'dashed', width: 1.5 },
      label: { formatter: label, color: COLORS.cyan, fontSize: 10, position: 'insideEndTop' },
      data: [{ xAxis: xData[idx] }],
    },
  };
}

/**
 * Line Chart Component - Requests/Visitors over time
 */
export function TimeSeriesChart({ title, data, xKey = 'date', series, height = 280, infoKey }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const xData = data.map((d) => {
      const date = new Date(d[xKey]);
      return `${date.getDate()}.${date.getMonth() + 1}`;
    });

    const option = {
      ...chartTheme,
      grid: { left: 50, right: 20, top: 30, bottom: 30 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: {
        ...chartTheme.legend,
        right: 0,
        top: 0,
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: series.map((s, idx) => ({
        name: s.name,
        type: 'line',
        data: data.map((d) => d[s.key] || 0),
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: s.color || [COLORS.orange, COLORS.cyan, COLORS.green][idx],
        },
        itemStyle: {
          color: s.color || [COLORS.orange, COLORS.cyan, COLORS.green][idx],
        },
        areaStyle: s.area
          ? {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: (s.color || COLORS.orange) + '40' },
                { offset: 1, color: (s.color || COLORS.orange) + '05' },
              ]),
            }
          : null,
      })),
    };

    chartInstance.current.setOption(option);

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, title, series]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <ChartHeader title={title} infoKey={infoKey} />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <ChartHeader title={title} infoKey={infoKey} />
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  );
}

/**
 * Bar Chart Component - Horizontal bars for rankings
 */
export function HorizontalBarChart({
  title,
  data,
  labelKey,
  valueKey,
  color = COLORS.orange,
  height = 280,
  infoKey,
  labelFormatter,
}) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    let labels = data.map((d) => (Array.isArray(d) ? d[0] : d[labelKey]));
    const values = data.map((d) => (Array.isArray(d) ? d[1] : d[valueKey]));
    const originalLabels = [...labels];

    if (labelFormatter) {
      labels = labels.map((l) => labelFormatter(l));
    }

    const truncatedLabels = labels.map((l) =>
      l && l.length > 25 ? l.substring(0, 25) + '...' : l
    );

    const option = {
      ...chartTheme,
      grid: { left: 150, right: 40, top: 10, bottom: 20 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const idx = params[0].dataIndex;
          return `${labels[idx]}: ${values[idx].toLocaleString('de-DE')}`;
        },
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      yAxis: {
        type: 'category',
        data: truncatedLabels.reverse(),
        axisLine: { show: false },
        axisLabel: {
          color: COLORS.gray,
          fontSize: 11,
          width: 140,
          overflow: 'truncate',
        },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar',
          data: values.reverse(),
          barWidth: '60%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: color },
              { offset: 1, color: color + 'cc' },
            ]),
            borderRadius: [0, 4, 4, 0],
          },
        },
      ],
    };

    chartInstance.current.setOption(option);

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, title, color]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <ChartHeader title={title} infoKey={infoKey} />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <ChartHeader title={title} infoKey={infoKey} />
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  );
}

/**
 * Requests by Hour Chart - Shows usage patterns
 */
export function HourlyDistributionChart({ title, data, height = 250, infoKey }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || Object.keys(data).length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const values = hours.map((h) => data[h] || 0);

    const option = {
      ...chartTheme,
      grid: { left: 50, right: 20, top: 10, bottom: 30 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        formatter: (params) => `${params[0].axisValue}:00: ${params[0].value} requests`,
      },
      xAxis: {
        type: 'category',
        data: hours.map((h) => `${h}`),
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: {
          color: COLORS.gray,
          fontSize: 11,
          formatter: (v) => `${v}h`,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: '70%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: COLORS.cyan },
              { offset: 1, color: COLORS.cyan + '60' },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };

    chartInstance.current.setOption(option);

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, title]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <ChartHeader title={title} infoKey={infoKey} />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <ChartHeader title={title} infoKey={infoKey} />
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  );
}

/**
 * Pie/Donut Chart - For proportional data
 */
export function DonutChart({ title, data, height = 250, infoKey }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const colors = [COLORS.orange, COLORS.cyan, COLORS.green, COLORS.purple, COLORS.red];

    const option = {
      ...chartTheme,
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        ...chartTheme.legend,
        orient: 'vertical',
        right: 10,
        top: 'center',
      },
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['35%', '55%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#0f172a',
            borderWidth: 2,
          },
          label: { show: false },
          labelLine: { show: false },
          data: data.map((d, i) => ({
            name: d.name,
            value: d.value,
            itemStyle: { color: colors[i % colors.length] },
          })),
        },
      ],
    };

    chartInstance.current.setOption(option);

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, title]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <ChartHeader title={title} infoKey={infoKey} />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <ChartHeader title={title} infoKey={infoKey} />
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  );
}

/**
 * Bar + Line Combo Chart — bars on left axis (counts), line on right axis (rate %).
 * Used for funnel-trend charts (LF1, LF5).
 */
export function BarLineComboChart({
  title,
  data,
  xKey = 'bucket_start',
  barKey,
  barLabel,
  barColor = COLORS.orange,
  lineKey,
  lineLabel,
  lineColor = COLORS.cyan,
  yAxisLabels = { left: 'Count', right: 'Rate (%)' },
  height = 320,
  infoKey,
  xFormatter,
  reliableFrom,
}) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const fmt =
      xFormatter ||
      ((v) => {
        const d = new Date(v);
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
      });
    const xData = data.map((d) => fmt(d[xKey]));
    const rel = reliabilityMarkers(data, xKey, xData, reliableFrom);

    const option = {
      ...chartTheme,
      // Extra top padding so the legend (now at top: 8) doesn't collide with
      // the left/right Y-axis name labels that sit on the top edge.
      grid: { left: 60, right: 60, top: 50, bottom: 30 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis', axisPointer: { type: 'cross' } },
      legend: { ...chartTheme.legend, right: 8, top: 8 },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { show: false },
      },
      yAxis: [
        {
          // Left Y axis (counts). nameLocation 'middle' rotates the name
          // and sits it against the axis, eliminating the overlap with the
          // legend that used to sit at top-right.
          type: 'value',
          name: yAxisLabels.left,
          nameLocation: 'middle',
          nameGap: 40,
          nameRotate: 90,
          nameTextStyle: { color: COLORS.gray, fontSize: 11 },
          axisLine: { show: false },
          axisLabel: { color: COLORS.gray, fontSize: 11 },
          splitLine: { lineStyle: { color: COLORS.grid } },
        },
        {
          // Right Y axis (rate %). Same treatment as left axis.
          type: 'value',
          name: yAxisLabels.right,
          nameLocation: 'middle',
          nameGap: 40,
          nameRotate: 90,
          nameTextStyle: { color: COLORS.gray, fontSize: 11 },
          axisLine: { show: false },
          axisLabel: { color: COLORS.gray, fontSize: 11, formatter: '{value}%' },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: barLabel,
          type: 'bar',
          yAxisIndex: 0,
          // Plain `color` is what the legend marker reads. The pretty
          // gradient stays on the bars themselves via itemStyle.color.
          color: barColor,
          data: data.map((d) => d[barKey] || 0),
          barWidth: '60%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: barColor },
              { offset: 1, color: barColor + '88' },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
          ...(rel ? { markArea: rel.markArea, markLine: rel.markLine } : {}),
        },
        {
          name: lineLabel,
          type: 'line',
          yAxisIndex: 1,
          data: data.map((d) => d[lineKey] || 0),
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          color: lineColor,
          lineStyle: { width: 2, color: lineColor },
          itemStyle: { color: lineColor },
        },
      ],
    };

    chartInstance.current.setOption(option);
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, title, barKey, lineKey, reliableFrom]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <ChartHeader title={title} infoKey={infoKey} />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <ChartHeader title={title} infoKey={infoKey} />
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  );
}

/**
 * Stacked Area Chart — multiple smooth areas stacked to a total.
 * With cumulative=true, applies running-sum across buckets per series (LF6).
 */
export function StackedAreaChart({
  title,
  data,
  xKey = 'bucket_start',
  series,
  cumulative = false,
  height = 320,
  infoKey,
  xFormatter,
  reliableFrom,
}) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    }

    const transformed = cumulative
      ? data.reduce((acc, row, i) => {
          const prev = acc[i - 1] || {};
          const next = { [xKey]: row[xKey] };
          series.forEach((s) => {
            next[s.key] = (prev[s.key] || 0) + (row[s.key] || 0);
          });
          acc.push(next);
          return acc;
        }, [])
      : data;

    const fmt =
      xFormatter ||
      ((v) => {
        const d = new Date(v);
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
      });
    const xData = transformed.map((d) => fmt(d[xKey]));
    const rel = reliabilityMarkers(transformed, xKey, xData, reliableFrom);

    const option = {
      ...chartTheme,
      grid: { left: 60, right: 20, top: 30, bottom: 30 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis', axisPointer: { type: 'cross' } },
      legend: { ...chartTheme.legend, right: 0, top: 0 },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: series.map((s, i) => ({
        name: s.name,
        type: 'line',
        stack: 'total',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false, // hide on chart, keep visible in legend marker
        ...(i === 0 && rel ? { markArea: rel.markArea, markLine: rel.markLine } : {}),
        // Both lineStyle.color AND itemStyle.color must be set so the
        // legend marker (which uses itemStyle) matches the line drawn on
        // the canvas (which uses lineStyle). Without itemStyle ECharts
        // falls back to its default colour palette and the marker no
        // longer matches the series colour.
        itemStyle: { color: s.color },
        lineStyle: { width: 1, color: s.color },
        emphasis: { focus: 'series' },
        areaStyle: {
          opacity: 0.7,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: (s.color || COLORS.orange) + 'A0' },
            { offset: 1, color: (s.color || COLORS.orange) + '20' },
          ]),
        },
        data: transformed.map((d) => d[s.key] || 0),
      })),
    };

    chartInstance.current.setOption(option);
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data, title, series, cumulative, reliableFrom]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <ChartHeader title={title} infoKey={infoKey} />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <ChartHeader title={title} infoKey={infoKey} />
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  );
}
